import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/misc';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { isApiError } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { useMe, useUpdateMe } from '@/lib/queries';
import { SettingsLayout } from './settings-layout';

const COMMON_LOCALES = ['en', 'en-GB', 'en-US', 'it', 'de', 'fr', 'es', 'nl', 'pt', 'ja'];

function timeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  const list = intl.supportedValuesOf?.('timeZone') ?? [
    'UTC',
    'Europe/Rome',
    'Europe/London',
    'America/New_York',
  ];
  return list.includes('UTC') ? list : ['UTC', ...list];
}

export function ProfilePage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const update = useUpdateMe();
  const zones = useMemo(timeZones, []);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [timeZone, setTimeZone] = useState('UTC');
  const [locale, setLocale] = useState('');
  const [showPreviousActivities, setShowPreviousActivities] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!me) return;
    setFirstName(me.firstName);
    setLastName(me.lastName);
    setTimeZone(me.timeZone);
    setLocale(me.locale ?? '');
    setShowPreviousActivities(me.showPreviousActivities);
  }, [me]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErrors({});
    update.mutate(
      { firstName, lastName, timeZone, locale: locale || null, showPreviousActivities },
      {
        onSuccess: () => toast.success(t('settings.profileSaved')),
        onError: (err) => {
          if (isApiError(err)) setErrors(err.fieldErrors);
          toast.error(describeError(err, t));
        },
      },
    );
  };

  return (
    <SettingsLayout>
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.profile')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('settings.firstName')} error={errors.firstName} required>
                {(p) => (
                  <Input
                    {...p}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                    required
                  />
                )}
              </Field>
              <Field label={t('settings.lastName')} error={errors.lastName} required>
                {(p) => (
                  <Input
                    {...p}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                    required
                  />
                )}
              </Field>
            </div>
            <Field label={t('settings.email')} hint={t('settings.emailHelp')}>
              {(p) => <Input {...p} value={me?.email ?? ''} readOnly disabled />}
            </Field>
            <Field
              label={t('settings.timeZone')}
              hint={t('settings.timeZoneHelp')}
              error={errors.timeZone}
              required
            >
              {(p) => (
                <Select value={timeZone} onValueChange={setTimeZone}>
                  <SelectTrigger id={p.id} aria-describedby={p['aria-describedby']}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map((z) => (
                      <SelectItem key={z} value={z}>
                        {z}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field
              label={t('settings.locale')}
              hint={t('settings.localeHelp')}
              error={errors.locale}
            >
              {(p) => (
                <Select
                  value={locale || '__default'}
                  onValueChange={(v) => setLocale(v === '__default' ? '' : v)}
                >
                  <SelectTrigger id={p.id} aria-describedby={p['aria-describedby']}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default">
                      {me?.organization.locale ?? 'en'} (organization default)
                    </SelectItem>
                    {COMMON_LOCALES.map((l) => (
                      <SelectItem key={l} value={l}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <fieldset className="space-y-1.5">
              <legend className="text-sm font-medium">{t('settings.timesheet')}</legend>
              {/* A checkbox rather than a Field: the label belongs beside the control, and the
                  explanation is long enough that it has to be readable without hovering. */}
              <label className="flex gap-3 rounded-md border p-3 hover:bg-accent/40">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0"
                  checked={showPreviousActivities}
                  onChange={(e) => setShowPreviousActivities(e.target.checked)}
                />
                <span className="space-y-1">
                  <span className="block font-medium">{t('settings.previousActivities')}</span>
                  <span className="block text-sm text-muted-foreground">
                    {t('settings.previousActivitiesHelp')}
                  </span>
                </span>
              </label>
            </fieldset>
            <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
              <div className="font-medium text-foreground">
                {t('settings.organization')}: {me?.organization.name}
              </div>
              <div>
                {t('settings.weekStart')}: {t(`weekdays.${me?.organization.weekStartDay ?? 1}`)} ·{' '}
                {t('settings.hoursFormat')}:{' '}
                {me?.organization.hoursFormat === 'HH_MM' ? 'hh:mm' : '7.50'}
              </div>
            </div>
            <Button type="submit" disabled={update.isPending}>
              {t('app.save')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
